import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, User } from '../lib/supabase';
import { normalizeWhatsApp } from '../lib/utils';
import { logger } from '../lib/logger';

interface AuthContextType {
  user: any | null;
  profile: User | null;
  loading: boolean;
  status: 'loading' | 'authenticated_loading_profile' | 'ready' | 'missing_profile';
  isRetrying: boolean;
  resolvedRole: string | null;
  forceReady: () => void;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<User | null>;
  // Centralized Custom Auth Helpers (strictly using profile.role as requested)
  hasRole: (roles: string | string[]) => boolean;
  isAdmin: () => boolean;
  canManageBlog: () => boolean;
  canAccessDashboard: () => boolean;
  // Standard Email/Password Flow
  loginWithEmailPassword: (email: string, password: string) => Promise<{ success: boolean; isUnverified?: boolean; email?: string; error?: string; profile?: User; profileError?: boolean }>;
  signUpPartner: (email: string, password: string, profileData: any, businessData: any, tenantId?: string) => Promise<{ success: boolean; error?: string }>;
  signUpCustomer: (email: string, password: string, fullName: string, whatsappNumber: string) => Promise<{ success: boolean; error?: string; profile?: User }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Helper to wrap a promise with a timeout and optional retries
 */
const withTimeout = async <T,>(
  promiseFn: (signal: AbortSignal) => Promise<T>, 
  timeoutMs: number, 
  errorMessage: string,
  retries = 1,
  onRetry?: (retryNum: number) => void
): Promise<T> => {
  let lastError: any;
  
  for (let i = 0; i <= retries; i++) {
    if (onRetry) onRetry(i);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      logger.warn('AUTH', `Timing out attempt ${i + 1} after ${timeoutMs}ms`);
      controller.abort();
    }, timeoutMs);

    try {
      if (i > 0) logger.info('AUTH', `Retry attempt ${i}...`);
      
      const timeoutPromise = new Promise<never>((_, reject) => {
        if (controller.signal.aborted) {
          reject(new Error(errorMessage));
          return;
        }
        controller.signal.addEventListener('abort', () => reject(new Error(errorMessage)), { once: true });
      });

      const result = await Promise.race([promiseFn(controller.signal), timeoutPromise]);
      return result as T;
    } catch (err: any) {
      lastError = err;
      let finalMessage = err.message;
      
      if (err.name === 'AbortError') {
        finalMessage = errorMessage;
      }
      
      logger.warn('AUTH', `Attempt ${i + 1} failed: ${finalMessage}`);
      
      // If it's a timeout or network error, retry
      if (finalMessage === errorMessage || finalMessage?.includes('Failed to fetch')) {
        if (i < retries) {
          const delay = 2000 * (i + 1);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
      }
      // If it's not a retryable error, wrap it in the final message if it was an abort
      if (err.name === 'AbortError') {
        throw new Error(errorMessage);
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  }
  
  throw lastError;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [profile, setProfile] = useState<User | null>(null);
  const profileRef = React.useRef<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'loading' | 'authenticated_loading_profile' | 'ready' | 'missing_profile'>('loading');
  const [isRetrying, setIsRetrying] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const isMounted = React.useRef(true);
  const pendingProfiles = React.useRef<Record<string, Promise<User | null>>>({});
  const isHydrating = React.useRef(true);

  const resolvedRole = React.useMemo(() => {
    if (!profile?.role) return null;
    const roleStr = (profile.role as string) === 'seller' ? 'business' : profile.role;
    console.log(`[AuthRole] Centralized role resolved synchronously from user_profiles table: ${roleStr}`);
    return roleStr;
  }, [profile]);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  /**
   * Fetch profile from user_profiles table using auth_user_id
   * Includes a 10s internal timeout to prevent hanging.
   */
  const fetchProfile = async (userId: string, retryCount = 0, force = false): Promise<User | null> => {
    if (!userId) return null;
    if (!force && pendingProfiles.current[userId]) return pendingProfiles.current[userId];

    const profilePromise = (async () => {
      try {
        console.log(`[AuthProfile] Fetching canonical profile for UID ${userId} (Attempt ${retryCount + 1})`);
        // Resolve the canonical profile through a SECURITY DEFINER RPC.
        // This avoids coupling authentication to the accumulated/legacy
        // user_profiles SELECT policies while still enforcing auth.uid() server-side.
        const result = await Promise.race([
          supabase.rpc('get_my_user_profile'),
          new Promise<any>((_, reject) => setTimeout(() => reject(new Error('Profile query timeout')), 10000))
        ]);
        if (result.error) throw result.error;

        // The RPC returns one profile row or null.
        const rows = Array.isArray(result.data) ? result.data : (result.data ? [result.data] : []);
        if (rows.length > 1) {
          throw new Error('Multiple profiles found for the authenticated account.');
        }
        const data = rows[0] || null;

        if (!data) {
          console.warn(`[AuthProfile] No profile found for authenticated UID ${userId}`);
          return null;
        }

        console.log(`[AuthProfile] Profile fetched successfully. DB Role: ${data.role}`);

        if (data.role === 'seller') data.role = 'business';

        if (!data.role) {
          console.error(`[AuthProfile] Profile ${data.id} has no role. Refusing to default to customer.`);
          return null;
        }

        // Do not write to user_profiles during authentication. Authentication
        // must be read-only; email synchronization belongs to profile management.
        if (!data.email && user?.email) data.email = user.email;

        if (isMounted.current) {
          setProfile(data);
          profileRef.current = data;
        }
        return data;
      } catch (err: any) {
        console.error(`[AuthProfile] Profile fetch failed: ${err.message}`);
        if (retryCount < 2 && isMounted.current && !err.message.includes('timeout')) {
          await new Promise(resolve => setTimeout(resolve, 1500));
          if (isMounted.current) {
            delete pendingProfiles.current[userId];
            return fetchProfile(userId, retryCount + 1, force);
          }
        }
        return null;
      } finally {
        setTimeout(() => {
          if (pendingProfiles.current && userId) delete pendingProfiles.current[userId];
        }, 1500);
      }
    })();

    pendingProfiles.current[userId] = profilePromise;
    return profilePromise;
  };

  /**
   * HYPER-FAILSAFE: Manually forces the auth state to ready.
   * Useful for breaking infinite loading loops in unstable environments.
   */
  const forceReady = () => {
    // Kept for API compatibility, but never bypass authentication/profile resolution.
    // A UI control must not be able to force an authenticated state.
    console.warn('[Auth] forceReady is disabled; authentication must resolve normally.');
  };

  const refreshProfile = async (): Promise<User | null> => {
    if (user?.id) {
      try {
        const updatedProfile = await fetchProfile(user.id, 0, true);
        if (updatedProfile) {
          setProfile(updatedProfile);
          profileRef.current = updatedProfile;
          return updatedProfile;
        }
      } catch (err) {
        console.error('Auth: refreshProfile failed', err);
      }
    }
    return null;
  };

  // STABILITY RULE: Track exact boot time to suppress events in first 2s
  const bootTime = React.useRef(Date.now());

  useEffect(() => {
    // 1. Unified Hydration Flow
    const hydrateAuth = async () => {
      console.log('[AuthContext] Starting Hydration...');
      
      // Safety switch: Force clear loading after 8s no matter what (STABILITY RULE)
      const safetyId = setTimeout(() => {
        if (isHydrating.current) {
          console.warn('[AuthContext] SAFETY TRIGGERED. Hydration stalled. Unlocking UI.');
          setLoading(false);
          setStatus('ready');
          isHydrating.current = false;
        }
      }, 8000);

      try {
        console.log('[AuthContext] Phase 1: getSession');
        // Add specific timeout for getSession as it can hang in some environments
        const sessionResult = await Promise.race([
          supabase.auth.getSession(),
          new Promise<any>((_, r) => setTimeout(() => r(new Error('getSession timeout')), 10000))
        ]);
        
        const { data: { session }, error: sessionError } = sessionResult;
        
        if (sessionError) {
          console.error('[AuthContext] sessionError:', sessionError);
        }
        
        if (!isMounted.current) return;

        const authUser = session?.user || null;
        setUser(authUser);
        if (authUser) {
          console.log('[AuthContext] Session Active. UID:', authUser.id);
          console.log('[AuthContext] User Metadata:', authUser.user_metadata);
        }

        if (authUser) {
          console.log('[AuthContext] Phase 2: User found, fetching profile', authUser.id);
          setStatus('authenticated_loading_profile');
          
          try {
            // Fetch profile with its own internal timeout
            const userProfile = await fetchProfile(authUser.id);
            
            if (isMounted.current) {
              if (userProfile) {
                console.log('[AuthContext] Phase 3: Profile loaded successfully from database');
                setProfile(userProfile);
                profileRef.current = userProfile;
                setStatus('ready');
              } else {
                console.warn('[AuthContext] Phase 3: No profile record found in user_profiles.');
                setStatus('missing_profile');
              }
            }
          } catch (profileErr: any) {
            console.error('[AuthContext] Profile fetch error during hydration:', profileErr.message);
            if (isMounted.current) setStatus('missing_profile');
          }
        } else {
          console.log('[AuthContext] Phase 2: No session');
          setStatus('ready');
        }
      } catch (err: any) {
        console.error('[AuthContext] Hydration failed or timed out:', err.message);
      } finally {
        clearTimeout(safetyId);
        // ENSURE STATE SETTLES UNCONDITIONALLY to prevent infinite loaders
        setLoading(false);
        isHydrating.current = false;
        console.log('[AuthContext] Hydration settled. Final Status:', status);

      }
    };

    hydrateAuth();

    // 2. Event Listener for background changes (Login/Logout)
    // IMPORTANT: Supabase documents that async Supabase calls inside
    // onAuthStateChange can deadlock the client. Keep this callback synchronous
    // and defer all profile/database work outside the auth callback.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const authUser = session?.user || null;
      console.log(`[AuthLifecycle] Event received: ${event}`);

      if (!authUser) {
        if (isMounted.current) {
          setUser(null);
          setProfile(null);
          profileRef.current = null;
          setStatus('ready');
          setLoading(false);
        }
        return;
      }

      if (isMounted.current) {
        setUser(authUser);
      }

      // Do not call Supabase APIs synchronously from this callback.
      setTimeout(() => {
        if (!isMounted.current) return;

        // If hydration/login already resolved the canonical profile, nothing more is needed.
        if (profileRef.current?.auth_user_id === authUser.id && profileRef.current?.role) {
          console.log('[AuthLifecycle] Profile already matched, skipping deferred fetch');
          setStatus('ready');
          setLoading(false);
          return;
        }

        setStatus('authenticated_loading_profile');

        void fetchProfile(authUser.id)
          .then((userProfile) => {
            if (!isMounted.current) return;

            if (userProfile) {
              setProfile(userProfile);
              profileRef.current = userProfile;
              setStatus('ready');
            } else {
              console.warn('[AuthLifecycle] Deferred profile fetch returned no profile');
              setStatus('missing_profile');
            }
          })
          .catch((err: any) => {
            console.error('[AuthLifecycle] Deferred profile fetch failed:', err?.message || err);
            if (isMounted.current) setStatus('missing_profile');
          })
          .finally(() => {
            if (isMounted.current) setLoading(false);
          });
      }, 0);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Removed redundant testConnection as it's now part of runDiagnostics
  }, []);

  const loginWithEmailPassword = async (email: string, password: string) => {
    if (isAuthenticating) return { success: false, error: 'Login already in progress.' };

    try {
      setIsAuthenticating(true);
      const cleanEmail = email.trim();
      const cleanPassword = password; // Password is an exact credential; preserve every character.
      // logger.info('AUTH', `SignIn started for: ${cleanEmail}`);

      // Create a promise that resolves if onAuthStateChange detects a SIGNED_IN event
      // This acts as a safety net if the signInWithPassword promise hangs
      let authEventDetected = false;
      const authEventPromise = new Promise<{ user: any }>((resolve) => {
        const checkAuth = setInterval(() => {
          if (user && !authEventDetected) {
            authEventDetected = true;
            console.log('Auth: Safety net detected active user session');
            clearInterval(checkAuth);
            resolve({ user });
          }
        }, 1000);
        
        // Clean up interval after 30s
        setTimeout(() => clearInterval(checkAuth), 30000);
      });

      const signInPromise = (async () => {
        const { data, error } = await supabase.auth.signInWithPassword({ 
          email: cleanEmail, 
          password: cleanPassword 
        });
        if (error) throw error;
        return data;
      })();

      console.log('Auth: signInWithPassword call initiated...');
      const data = await withTimeout(
        async (signal) => await Promise.race([signInPromise, authEventPromise]),
        25000,
        'Authentication timed out — check your internet or Supabase project status',
        1 // Reduce retries to avoid long wait times
      ) as any;

      if (!data.user) throw new Error('No user data returned from Supabase.');

      // We MUST wait for the profile during login to know where to navigate the user
      // FORCED refresh to ensure we don't use a cached role from previous session/state
      let userProfile = await fetchProfile(data.user.id, 0, true);
      
      logger.audit('AUTH_LOGIN', `User ${data.user.email} signed in`, { userId: data.user.id, role: userProfile?.role });
      if (!userProfile?.role) {
        console.error('[Auth] Authentication succeeded but canonical database profile/role could not be resolved. Blocking dashboard entry.');
        // Keep the authenticated Supabase user in context so the UI can distinguish
        // a profile-resolution problem from invalid credentials and recover cleanly.
        if (isMounted.current) {
          setUser(data.user);
          setProfile(null);
          profileRef.current = null;
          setLoading(false);
          setStatus('missing_profile');
        }
        return {
          success: false,
          profileError: true,
          error: 'Your account was authenticated, but your MyServiceHub profile could not be loaded. Please refresh and try again.'
        };
      }

      if (isMounted.current) {
        setProfile(userProfile);
        profileRef.current = userProfile;
        setLoading(false);
        setStatus('ready');
      }

      setUser(data.user);
      return { success: true, profile: userProfile };
    } catch (error: any) {
      const errMsg = error.message || '';
      const isUnverifiedErr = errMsg.toLowerCase().includes('email not confirmed') || 
                              errMsg.toLowerCase().includes('email_not_confirmed') ||
                              error?.code === 'email_not_confirmed' ||
                              error?.status === 400 && errMsg.toLowerCase().includes('confirm');

      if (isUnverifiedErr) {
        return {
          success: false,
          isUnverified: true,
          email,
          error: 'Please verify your email before signing in. Check your inbox and spam folder.'
        };
      }

      logger.error('AUTH_LOGIN_FAIL', error.message, { email });
      
      // If we timed out but the user is actually signed in (safety check)
      if (error.message.includes('timed out') && user) {
        logger.info('AUTH_RECOVERY', 'User is signed in despite timeout. Fetching profile...');
        const recoveredProfile = await fetchProfile(user.id);
        if (recoveredProfile) {
          setProfile(recoveredProfile);
          profileRef.current = recoveredProfile;
          return { success: true, profile: recoveredProfile };
        }
      }

      return { success: false, error: error.message };
    } finally {
      setIsAuthenticating(false);
    }
  };

  const signUpPartner = async (email: string, password?: string, profileData?: any, businessData?: any, tenantId?: string) => {
    try {
      const cleanEmail = email.trim();
      const cleanPassword = password; // Passwords must be preserved exactly.
      console.log('Auth: signUpPartner started for:', cleanEmail);
      logger.info('AUTH_SIGNUP', `signUpPartner initiated for email: ${cleanEmail}`);

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(cleanEmail)) {
        throw new Error('Please provide a valid email address.');
      }

      // Get current auth session token if user is already logged in (upgrade mode)
      const session = (await supabase.auth.getSession()).data.session;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      // Call our secure backend registration endpoint
      const response = await fetch('/api/auth/register-partner', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: cleanEmail,
          password: cleanPassword,
          fullName: profileData?.fullName,
          whatsappNumber: profileData?.whatsappNumber,
          businessType: profileData?.businessType,
          businessName: businessData?.name,
          country: businessData?.country,
          tenantId: tenantId,
          subscriptionPlan: businessData?.subscriptionPlan,
          identityNumber: businessData?.identityNumber,
          officeAddress: businessData?.officeAddress,
          docs: businessData?.docs,
          referralCode: businessData?.referralCode || businessData?.ref
        })
      });

      const resData = await response.json();
      if (!response.ok || !resData.success) {
        throw new Error(resData.error || 'Failed to complete partner registration.');
      }

      // If new account with password, sign in automatically on the client to establish active session
      if (cleanPassword && !user) {
        try {
          const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({
            email: cleanEmail,
            password: cleanPassword
          });

          if (!signInErr && signInData?.user) {
            setUser(signInData.user);
            const userProfile = await fetchProfile(signInData.user.id, 0, true);
            if (userProfile && isMounted.current) {
              setProfile(userProfile);
              profileRef.current = userProfile;
            }
          }
        } catch (signInEx) {
          console.warn('[Auth] Auto sign-in after registration warning:', signInEx);
        }
      } else if (user) {
        await refreshProfile();
      }

      logger.audit('AUTH_SIGNUP_SUCCESS', `signUpPartner completed successfully for email: ${cleanEmail}`, { email: cleanEmail });
      return { success: true };
    } catch (error: any) {
      console.error('Auth: error details: ' + error.message);
      logger.error('AUTH_SIGNUP_FAIL', `signUpPartner crashed/failed for ${email}: ${error.message}`, { email, errorMsg: error.message });
      return { success: false, error: error.message };
    }
  };

  const signUpCustomer = async (email: string, password: string, fullName: string, whatsappNumber: string) => {
    try {
      const cleanEmail = email.trim();
      const cleanPassword = password; // Passwords must be preserved exactly.
      console.log('Auth: signUpCustomer started for:', cleanEmail);
      logger.info('AUTH_SIGNUP', `signUpCustomer initiated for email: ${cleanEmail}`);
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(cleanEmail)) {
        throw new Error('Please provide a valid email address.');
      }

      const normalizedWhatsApp = normalizeWhatsApp(whatsappNumber);

      // Customer signup is strictly non-privileged customer role
      const determinedRole = 'customer';
      const determinedVerification = 'none';
      
      const { data: authData, error: authError } = await withTimeout(
        async (signal) => await supabase.auth.signUp({ 
          email: cleanEmail, 
          password: cleanPassword,
          options: {
            data: {
              full_name: fullName,
              whatsapp_number: normalizedWhatsApp,
              role: determinedRole
            }
          }
        }),
        20000,
        'Sign up timed out',
        0 // Disable retries for sensitive Auth.signUp to avoid duplicate calls and bounced emails
      ) as any;

      if (authError) {
        logger.error('AUTH_SIGNUP_FAIL', `Auth signUpCustomer failed for ${cleanEmail}: ${authError.message}`, { email: cleanEmail, code: authError.status });
        if (authError.status === 429) {
          throw new Error('Too many signup attempts. Please wait a few minutes before trying again.');
        }
        throw authError;
      }
      if (!authData.user) throw new Error('Failed to create auth user.');

      // 1. Check if profile already exists for this WhatsApp or auth_user_id
      let existingProfile: any = null;
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('id')
          .or(`auth_user_id.eq.${authData.user.id},whatsapp_number.eq.${normalizedWhatsApp}`)
          .maybeSingle();
        existingProfile = data;
      } catch (err) {
        console.warn('Auth: signUpCustomer profile check error (ignoring and using fallback):', err);
      }

      // 2. Create/Update User Record (Optional since DB trigger handles creation)
      const profileToUpsert: any = {
        id: authData.user.id,
        auth_user_id: authData.user.id,
        full_name: fullName,
        whatsapp_number: normalizedWhatsApp,
        email: cleanEmail.toLowerCase(),
        role: determinedRole,
        verification_status: determinedVerification,
        agreed_to_terms: true,
        agreed_at: new Date().toISOString(),
        privacy_version: '2026-05-13'
      };

      if (existingProfile) {
        profileToUpsert.id = existingProfile.id;
      }

      let profileData: any = null;
      let profileError: any = null;

      try {
        if (existingProfile) {
          const { data, error } = await withTimeout(
            async (signal) => await supabase
              .from('user_profiles')
              .update({
                auth_user_id: authData.user.id,
                full_name: fullName,
                whatsapp_number: normalizedWhatsApp,
                email: cleanEmail.toLowerCase(),
                verification_status: determinedVerification,
                agreed_to_terms: true,
                agreed_at: new Date().toISOString(),
                privacy_version: '2026-05-13'
              })
              .eq('id', existingProfile.id)
              .select()
              .maybeSingle(),
            20000,
            'User record update timed out',
            1
          ) as any;
          profileData = data;
          profileError = error;
        } else {
          const defaultRoleToInsert = 'customer';
          const { data, error } = await withTimeout(
            async (signal) => await supabase
              .from('user_profiles')
              .upsert({
                ...profileToUpsert,
                role: defaultRoleToInsert
              }, { onConflict: 'whatsapp_number' })
              .select()
              .maybeSingle(),
            20000,
            'User record creation timed out',
            1
          ) as any;
          profileData = data;
          profileError = error;
        }

        if (profileError) {
          console.warn('Auth: signUpCustomer manual profile save error (using fallback):', profileError);
          profileData = {
            ...profileToUpsert,
            role: profileToUpsert.role || 'customer'
          };
        }
      } catch (err: any) {
        console.warn('Auth: signUpCustomer manual profile save exception (using fallback):', err);
        profileData = {
          ...profileToUpsert,
          role: profileToUpsert.role || 'customer'
        };
      }

      if (!profileData) {
        profileData = {
          ...profileToUpsert,
          role: profileToUpsert.role || 'customer'
        };
      }

      // Start profile fetch in background (non-blocking)
      setProfile(profileData);
      profileRef.current = profileData;
      setUser(authData.user);

      logger.audit('AUTH_SIGNUP_SUCCESS', `signUpCustomer completed successfully for email: ${cleanEmail}`, { email: cleanEmail, role: determinedRole });
      return { success: true, profile: profileData };
    } catch (error: any) {
      console.error('Auth: signUpCustomer error details: ' + error.message);
      logger.error('AUTH_SIGNUP_FAIL', `signUpCustomer crashed/failed for ${email}: ${error.message}`, { email, errorMsg: error.message });
      return { success: false, error: error.message };
    }
  };

  const hasRole = React.useCallback((roles: string | string[]): boolean => {
    // No profile = no role. Never fail open to customer.
    if (!resolvedRole) return false;
    return Array.isArray(roles) ? roles.includes(resolvedRole) : resolvedRole === roles;
  }, [resolvedRole]);

  const isAdmin = React.useCallback((): boolean => {
    if (!resolvedRole) return false;
    return ['platform_admin', 'admin'].includes(resolvedRole);
  }, [resolvedRole]);

  const canManageBlog = React.useCallback((): boolean => {
    if (!resolvedRole) return false;
    return ['platform_admin', 'admin'].includes(resolvedRole);
  }, [resolvedRole]);

  const canAccessDashboard = React.useCallback((): boolean => {
    if (!resolvedRole) return false;
    return ['platform_admin', 'admin', 'business', 'customer', 'tenant_manager', 'employee'].includes(resolvedRole);
  }, [resolvedRole]);

  const signOut = async () => {
    console.log('Auth: signing out...');
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    profileRef.current = null;
  };

  return (
    <AuthContext.Provider value={{ 
      user, profile, loading, status, isRetrying, resolvedRole, forceReady, signOut, refreshProfile, 
      hasRole, isAdmin, canManageBlog, canAccessDashboard,
      loginWithEmailPassword, signUpPartner, signUpCustomer 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// V7.1 - Finalizing Auth Logic Stability
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
